import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, Lock, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi, adminToken, messageFrom } from "@/lib/api";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!adminToken.get()) return;
    adminApi
      .me()
      .then(() => navigate("/admin", { replace: true }))
      .catch(() => adminToken.clear());
  }, [navigate]);

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const data = await adminApi.login(email.trim(), password);
      adminToken.set(data.token);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(messageFrom(err, "Those details don't match our records."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="flex min-h-screen flex-col bg-[color:var(--tt-surface-2)]"
    >
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 flex justify-center">
            <img src="/ttite-logo.png" alt="T-TITE" className="h-16 w-auto" />
          </Link>

          <form onSubmit={submit} className="tt-card p-6 sm:p-8">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[color:var(--tt-teal-mist)] text-primary">
              <Lock className="h-5 w-5" aria-hidden="true" />
            </span>
            <h1 className="mt-4 text-2xl font-bold text-foreground">Calculator dashboard</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              T-TITE staff only. Sign in to see every calculator session, lead and quote.
            </p>

            {error ? (
              <p
                role="alert"
                data-testid="admin-login-error"
                className="mt-5 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3.5 py-3 text-[13px] font-medium text-destructive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                {error}
              </p>
            ) : null}

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="admin-email" className="text-sm font-semibold">
                  Email address
                </Label>
                <Input
                  id="admin-email"
                  data-testid="admin-login-email-input"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="username"
                  placeholder="info@t-tite.co.za"
                  className="mt-1.5 h-12"
                  required
                />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-sm font-semibold">
                  Password
                </Label>
                <Input
                  id="admin-password"
                  data-testid="admin-login-password-input"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  className="mt-1.5 h-12"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit-button"
              className="mt-6 h-12 w-full text-base font-bold"
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <LogIn className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            <Link to="/" className="tt-link">
              Back to the pricing calculator
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
